import { useState, type ReactNode } from "react";
import { Icon, type IconName, Button, type Intent } from "@blueprintjs/core";

/**
 * Foundry-style shell banner — a flat strip of system notices that lives in
 * the `banners` row of the shell grid (above rail + header + main).
 *
 * Modeled on Foundry's `workspace-banner-components__wrapper` pattern.
 * Render via `<BannersZone banners={...} />`; pass an empty list to disable.
 */
export interface Banner {
  id: string;
  intent?: Intent;
  icon?: IconName;
  title: string;
  body?: string;
  /** Defaults to `true`. Set `false` to hide the dismiss button. */
  dismissible?: boolean;
  /** Optional extra controls rendered before the dismiss button (e.g. an
     "Acknowledge" link). */
  actions?: ReactNode;
}

/**
 * Renders a stack of `Banner` rows. Returns `null` when no banners are
 * visible — the grid `banners` row auto-collapses to zero height.
 *
 * Dismissal is local component state (per page load); persist via the
 * `onDismiss` callback if a banner needs to stay dismissed across reloads.
 */
export function BannersZone({
  banners,
  onDismiss,
}: {
  banners: readonly Banner[];
  onDismiss?: (id: string) => void;
}) {
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(new Set());
  const visible = banners.filter((b) => !dismissed.has(b.id));
  if (visible.length === 0) return null;

  return (
    <div
      className="bp-banners"
      role="region"
      aria-label="System notices"
      data-testid="banners-wrapper"
    >
      {visible.map((b) => (
        <div
          key={b.id}
          className={`bp-banner bp6-callout${b.intent ? ` bp6-intent-${b.intent}` : ""}`}
          role="status"
        >
          {b.icon && (
            <span className="bp-banner-icon" aria-hidden="true">
              <Icon icon={b.icon} />
            </span>
          )}
          <div className="bp-banner-text">
            <span className="bp-banner-title">{b.title}</span>
            {b.body && <span className="bp-banner-body">{b.body}</span>}
          </div>
          {b.actions && <div className="bp-banner-actions">{b.actions}</div>}
          {b.dismissible !== false && (
            <Button
              minimal
              small
              icon="cross"
              aria-label={`Dismiss ${b.title}`}
              className="bp-banner-dismiss"
              onClick={() => {
                setDismissed((prev) => new Set(prev).add(b.id));
                onDismiss?.(b.id);
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Example banner inventory — left in source as documentation of the shape.
 * Pass to `<BannersZone banners={EXAMPLE_BANNERS} />` to enable.
 * Currently disabled at the call site (empty list passed).
 */
export const EXAMPLE_BANNERS: readonly Banner[] = [
  {
    id: "dev-preview",
    intent: "primary",
    icon: "globe-network",
    title: "Development preview",
    body: "Connected to development data — refreshed nightly. Not for case work.",
    dismissible: true,
  },
];
